// Customer Administrator Page - Super Admin only
// View and manage all organizations/customers
import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Select,
  MenuItem,
  IconButton,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search,
  Business,
  People,
  Article,
  ConfirmationNumber,
  Edit,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { organizationsService } from '@/services/collections/organizations';
import type { Organization, PricingPlan } from '@/types';
import { PLAN_LIMITS, PLAN_PRICING } from '@/types';

const PLAN_COLORS: Record<PricingPlan, string> = {
  free: '#94a3b8',
  pro: '#3b82f6',
  business: '#8b5cf6',
  enterprise: '#f59e0b',
};

const PLAN_LABELS: Record<PricingPlan, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
};

export const CustomerAdminPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const { showSuccess, showError } = useToast();

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [newPlan, setNewPlan] = useState<PricingPlan>('free');

  // Redirect if not super admin
  useEffect(() => {
    if (!isSuperAdmin) {
      navigate('/app/dashboard');
    }
  }, [isSuperAdmin, navigate]);

  // Load all organizations
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        const orgs = await organizationsService.getAll();
        setOrganizations(orgs);
      } catch (err) {
        console.error('Error loading organizations:', err);
        showError('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, [showError]);

  const handlePlanChange = async () => {
    if (!selectedOrg) return;

    setChangingPlan(true);
    try {
      // Update organization plan and limits
      await organizationsService.update(selectedOrg.id, {
        plan: newPlan,
        limits: PLAN_LIMITS[newPlan],
      });

      // Update local state
      setOrganizations(prev =>
        prev.map(org =>
          org.id === selectedOrg.id
            ? { ...org, plan: newPlan, limits: PLAN_LIMITS[newPlan] }
            : org
        )
      );

      showSuccess(`Plan updated to ${PLAN_LABELS[newPlan]} for ${selectedOrg.name}`);
      setEditDialogOpen(false);
    } catch (err) {
      console.error('Error updating plan:', err);
      showError('Failed to update plan');
    } finally {
      setChangingPlan(false);
    }
  };

  const openEditDialog = (org: Organization) => {
    setSelectedOrg(org);
    setNewPlan(org.plan);
    setEditDialogOpen(true);
  };

  // Filter organizations by search
  const filteredOrgs = organizations.filter(org =>
    org.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.slug.toLowerCase().includes(searchQuery.toLowerCase()) ||
    org.billingEmail?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Calculate stats
  const stats = {
    total: organizations.length,
    free: organizations.filter(o => o.plan === 'free').length,
    pro: organizations.filter(o => o.plan === 'pro').length,
    business: organizations.filter(o => o.plan === 'business').length,
    enterprise: organizations.filter(o => o.plan === 'enterprise').length,
    totalSeats: organizations.reduce((sum, o) => sum + (o.usage?.seatCount || 0), 0),
    mrr: organizations.reduce((sum, o) => {
      const price = PLAN_PRICING[o.plan]?.monthly || 0;
      const seats = o.usage?.seatCount || 1;
      return sum + (price * seats);
    }, 0),
  };

  if (!isSuperAdmin) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight="bold" gutterBottom>
          Customer Administrator
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage all organizations and their subscription plans
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: 'grey.50' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Orgs</Typography>
              <Typography variant="h5" fontWeight="bold">{stats.total}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: '#f0f9ff' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Pro Plans</Typography>
              <Typography variant="h5" fontWeight="bold" color="primary">{stats.pro}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: '#faf5ff' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Business</Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#8b5cf6' }}>{stats.business}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: '#fffbeb' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Enterprise</Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#f59e0b' }}>{stats.enterprise}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: '#f0fdf4' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Total Seats</Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#10b981' }}>{stats.totalSeats}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 6, sm: 4, md: 2 }}>
          <Card sx={{ bgcolor: 'success.50', background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)' }}>
            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
              <Typography variant="caption" color="text.secondary">Est. MRR</Typography>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#16a34a' }}>
                ${(stats.mrr / 100).toFixed(0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search organizations by name, slug, or email..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {/* Organizations Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell><strong>Organization</strong></TableCell>
              {!isMobile && <TableCell><strong>Slug</strong></TableCell>}
              <TableCell><strong>Plan</strong></TableCell>
              {!isMobile && (
                <>
                  <TableCell align="center"><strong>Seats</strong></TableCell>
                  <TableCell align="center"><strong>Projects</strong></TableCell>
                  <TableCell align="center"><strong>Tickets</strong></TableCell>
                </>
              )}
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={isMobile ? 3 : 7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">
                    {searchQuery ? 'No organizations match your search' : 'No organizations found'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredOrgs.map((org) => (
                <TableRow key={org.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Business sx={{ color: 'grey.400', fontSize: 20 }} />
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {org.name}
                        </Typography>
                        {isMobile && (
                          <Typography variant="caption" color="text.secondary">
                            {org.slug}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  {!isMobile && (
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {org.slug}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip
                      label={PLAN_LABELS[org.plan]}
                      size="small"
                      sx={{
                        bgcolor: `${PLAN_COLORS[org.plan]}20`,
                        color: PLAN_COLORS[org.plan],
                        fontWeight: 'medium',
                      }}
                    />
                  </TableCell>
                  {!isMobile && (
                    <>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <People sx={{ fontSize: 16, color: 'grey.400' }} />
                          {org.usage?.seatCount || 0}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <Article sx={{ fontSize: 16, color: 'grey.400' }} />
                          {org.usage?.projectCount || 0}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                          <ConfirmationNumber sx={{ fontSize: 16, color: 'grey.400' }} />
                          {org.usage?.ticketCount || 0}
                        </Box>
                      </TableCell>
                    </>
                  )}
                  <TableCell align="right">
                    <Tooltip title="Change Plan">
                      <IconButton size="small" onClick={() => openEditDialog(org)}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Plan Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          Change Plan for {selectedOrg?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Current plan: <strong>{selectedOrg && PLAN_LABELS[selectedOrg.plan]}</strong>
            </Typography>
            <Select
              fullWidth
              value={newPlan}
              onChange={(e) => setNewPlan(e.target.value as PricingPlan)}
              size="small"
            >
              <MenuItem value="free">Free</MenuItem>
              <MenuItem value="pro">Pro ($7/seat/mo)</MenuItem>
              <MenuItem value="business">Business ($15/seat/mo)</MenuItem>
              <MenuItem value="enterprise">Enterprise (Custom)</MenuItem>
            </Select>
            {newPlan !== selectedOrg?.plan && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This will immediately update the organization's plan and feature limits.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={changingPlan}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handlePlanChange}
            disabled={changingPlan || newPlan === selectedOrg?.plan}
          >
            {changingPlan ? 'Updating...' : 'Update Plan'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
